import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader } from '@components/loader'
import {
  useExcludeFieldTranslation,
  useExcludeListValueTranslation,
  useUpdateFieldTranslationReview,
  useUpdateListValueTranslationReview,
} from '@global/hooks/mutations/taxonomy-translations'
import {
  useInvalidateTaxonomyTranslationFieldTranslations,
  useInvalidateTaxonomyTranslation,
  useTaxonomyTranslationFieldTranslations,
  useTaxonomyTranslationReferenceFields,
} from '@global/hooks/queries/taxonomy-translations'
import { cn } from '@global/utils/tailwind'
import { Modal, Button } from '@taxonomy-translation/components'
import { Tooltip } from '@components/tooltip/tooltip'
import {
  FieldTranslation,
  ListFieldTranslation,
  TaxonomyTranslation,
  Translation,
  TranslationStatus,
} from '@taxonomy-translation/types'
import {
  isListTranslation,
  trackItemReviewAction,
  trackModalAction,
} from '@taxonomy-translation/utils'
import { PreReviewStep, ReviewType } from './components/pre-review-step'
import { ReviewStep } from './components/review-step'
import { t } from '@lingui/core/macro'

interface TranslationReviewModalProps {
  translation: TaxonomyTranslation
  isOpen: boolean
  isReviewed: boolean
  onClose: () => void
}

export const TranslationReviewModal = ({
  translation,
  isOpen,
  isReviewed,
  onClose,
}: TranslationReviewModalProps) => {
  const { uuid: translationUuid, inputs = [] } = translation
  const invalidateTaxonomyTranslationFieldTranslations =
    useInvalidateTaxonomyTranslationFieldTranslations(translationUuid)
  const invalidateTaxonomyTranslation = useInvalidateTaxonomyTranslation(translationUuid)

  const [isInitialStep, setIsInitialStep] = useState(true)
  const [translationIndex, setTranslationIndex] = useState(0) // index for both field and list value
  const [reviewType, setReviewType] = useState<ReviewType>(ReviewType.ALL)
  const [translatedField, setTranslatedField] = useState<{ value: string; label: string } | null>(
    null,
  )
  const [translatedListValue, setTranslatedListValue] = useState<{
    value: string
    label: string
  } | null>(null)

  const { data: translationList = [], isLoading: isTranslationFieldFetching } =
    useTaxonomyTranslationFieldTranslations(translationUuid, {
      enabled: isOpen,
      staleTime: 1000 * 60 * 20,
    })

  const { data: referenceFields, isLoading: isReferenceFieldsFetching } =
    useTaxonomyTranslationReferenceFields(translationUuid, undefined, {
      enabled: isOpen,
      staleTime: 1000 * 60 * 20,
    })

  const { mutate: updateTranslationField, isPending: isUpdateTranslationFieldPending } =
    useUpdateFieldTranslationReview(translationUuid, {
      onSuccess: () => {
        moveToNextTranslation()
      },
    })
  const { mutate: updateListValue, isPending: isUpdateListValuePending } =
    useUpdateListValueTranslationReview(translationUuid, {
      onSuccess: () => {
        moveToNextTranslation()
      },
    })

  const { mutate: excludeTranslationField, isPending: isExcludeTranslationFieldPending } =
    useExcludeFieldTranslation(translationUuid, {
      onSuccess: () => {
        moveToNextTranslation(true)
      },
    })
  const { mutate: excludeListValue, isPending: isExcludeListValuePending } =
    useExcludeListValueTranslation(translationUuid, {
      onSuccess: () => {
        moveToNextTranslation(true)
      },
    })

  const counts = useMemo(
    (): Record<ReviewType, number> =>
      translationList.reduce(
        (acc, translation) => {
          const appliableReviewTypes = getAppliableReviewTypes(translation)

          for (const reviewType of appliableReviewTypes) {
            acc[reviewType]++
          }

          if (isListTranslation(translation)) {
            for (const listValueTranslation of translation.listValueTranslations) {
              const listValueAppliableReviewTypes = getAppliableReviewTypes(listValueTranslation)

              for (const reviewType of listValueAppliableReviewTypes) {
                acc[reviewType]++
              }
            }
          }

          return acc
        },
        {
          [ReviewType.ALL]: 0,
          [ReviewType.MATCH]: 0,
          [ReviewType.NO_MATCH]: 0,
          [ReviewType.REVIEWED]: 0,
          [ReviewType.ALL_EXCLUDE_CONFIDENCE]: 0,
          [ReviewType.ALL_EXCLUDE_REVIEWED]: 0,
        },
      ),
    [translationList],
  )

  const countByReviewType = useMemo(() => counts[reviewType], [counts, reviewType])

  const translationData = useMemo(
    () =>
      translationList.reduce((acc, translation) => {
        const newAcc = { ...acc, translations: [...acc.translations], indexes: [...acc.indexes] }

        const translationIndex = acc.translations.length

        const translationReviewTypeState = getTranslationReviewTypeState(translation)
        if (
          translationReviewTypeState[reviewType] ||
          (isListTranslation(translation) &&
            translation.listValueTranslations.some(
              listValueTranslation =>
                getTranslationReviewTypeState(listValueTranslation)[reviewType],
            ))
        ) {
          newAcc.translations.push(translation)

          if (
            translationReviewTypeState[reviewType] ||
            translation.status !== TranslationStatus.Reviewed
          ) {
            newAcc.indexes.push({ field: translationIndex })
          }

          if (isListTranslation(translation)) {
            for (const [index, listValueTranslation] of Object.entries(
              translation.listValueTranslations,
            )) {
              const listValueTranslationReviewTypeState =
                getTranslationReviewTypeState(listValueTranslation)

              if (listValueTranslationReviewTypeState[reviewType]) {
                newAcc.indexes.push({ field: translationIndex, listValue: Number(index) })
              }
            }
          }
        }

        return newAcc
      }, EMPTY_TRANSLATION_DATA),
    [reviewType, translationList],
  )

  const currentIndex = useMemo(
    () => translationData.indexes[translationIndex],
    [translationData, translationIndex],
  )

  const fieldTranslation = useMemo(
    () => getFieldTranslation(translationData, translationIndex),
    [translationData, translationIndex],
  )

  const isListValueEditing = useMemo(
    () => isListTranslation(fieldTranslation) && currentIndex?.listValue !== undefined,
    [fieldTranslation, currentIndex],
  )

  const onNextClick = useCallback(() => {
    if (isListTranslation(fieldTranslation) && currentIndex?.listValue !== undefined) {
      const listValueTranslation = getListValueTranslation(fieldTranslation, currentIndex.listValue)

      updateListValue({
        fieldId: fieldTranslation.id,
        index: listValueTranslation.index,
        expected: translatedListValue?.value ?? null,
      })
    } else {
      updateTranslationField({
        fieldId: fieldTranslation.id,
        expected: translatedField?.value ?? null,
      })
    }
    trackItemReviewAction({
      source: 'modal',
      action: 'reviewed',
    })
  }, [
    fieldTranslation,
    currentIndex,
    translatedField,
    translatedListValue,
    updateTranslationField,
    updateListValue,
  ])

  const onExcludeClick = useCallback(() => {
    if (isListTranslation(fieldTranslation) && currentIndex?.listValue !== undefined) {
      const listValueTranslation = getListValueTranslation(fieldTranslation, currentIndex.listValue)

      excludeListValue({
        fieldId: fieldTranslation.id,
        index: listValueTranslation.index,
      })
    } else {
      excludeTranslationField({
        fieldId: fieldTranslation.id,
      })
    }
    trackItemReviewAction({
      source: 'modal',
      action: 'excluded',
    })
  }, [fieldTranslation, currentIndex, excludeListValue, excludeTranslationField])

  const moveToNextTranslation = (isExcluding = false) => {
    // go to next translation
    if (translationIndex < countByReviewType - 1) {
      // in case of excluding in this step and next step is "list value" -> skip all "list value" steps
      // as they won't have options to be edited
      if (
        !isListValueEditing &&
        isExcluding &&
        typeof translationData.indexes[translationIndex + 1]?.listValue === 'number'
      ) {
        const fieldTranslation = getFieldTranslation(translationData, translationIndex)
        const skipCount = isListTranslation(fieldTranslation)
          ? fieldTranslation.listValueTranslations.length
          : 0

        setTranslationIndex(translationIndex + skipCount + 1)

        return
      }

      // or just go to next translation
      setTranslationIndex(prevValue => prevValue + 1)

      return
    }

    // finish review
    if (translationIndex === countByReviewType - 1) {
      handleClose()
    }
  }

  const handleSkip = useCallback(() => {
    moveToNextTranslation()
    trackItemReviewAction({
      source: 'modal',
      action: 'skipped',
    })
  }, [moveToNextTranslation])

  const handleClose = useCallback(() => {
    onClose()

    // make sure values in table are updated
    void invalidateTaxonomyTranslationFieldTranslations()

    // make sure values in dashboard are updated
    void invalidateTaxonomyTranslation()

    // reset modal values
    setIsInitialStep(true)
    setTranslationIndex(0)
    setReviewType(ReviewType.ALL)
    setTranslatedField(null)
    setTranslatedListValue(null)
  }, [
    onClose,
    invalidateTaxonomyTranslationFieldTranslations,
    invalidateTaxonomyTranslation,
    setIsInitialStep,
    setTranslationIndex,
    setReviewType,
    setTranslatedField,
    setTranslatedListValue,
  ])

  const handleDone = useCallback(() => {
    handleClose()
    trackModalAction('done')
  }, [handleClose])

  const handleExplicitClose = useCallback(() => {
    handleClose()
    trackModalAction('closed')
  }, [handleClose])

  // initialize selector value on each step
  useEffect(() => {
    if (!currentIndex) {
      return
    }

    const translation = translationData.translations[currentIndex.field]

    if (translation && isListTranslation(translation) && currentIndex?.listValue !== undefined) {
      // list value initialization
      const value = translation.listValueTranslations?.[currentIndex.listValue]?.translated

      setTranslatedListValue(value ? { value, label: value } : null)
    } else {
      // field initialization
      const value = translation?.translated

      setTranslatedField(value ? { value, label: value } : null)
      setTranslatedListValue(null)
    }
  }, [translationData, currentIndex])

  if (!isOpen) return null

  const hasTranslatedValue = !!(isListValueEditing ? translatedListValue : translatedField)
  const showExcludeWarning =
    !translatedField &&
    !isListValueEditing &&
    typeof translationData.indexes?.[translationIndex + 1]?.listValue === 'number' // list value is the next step

  return (
    <Modal
      onClose={handleClose}
      className={cn('p-0 w-[80vw] max-w-[1200px] flex flex-col justify-between', {
        'min-h-[440px]': !isInitialStep,
        'min-h-[400px]': isInitialStep,
      })}
    >
      {isTranslationFieldFetching || isReferenceFieldsFetching ? (
        <div className="flex items-center w-full h-[400px]">
          <Loader />
        </div>
      ) : (
        <>
          {isInitialStep ? (
            <PreReviewStep
              isReviewed={isReviewed}
              reviewType={reviewType}
              counts={counts}
              onReviewTypeChange={setReviewType}
            />
          ) : (
            !!fieldTranslation &&
            !!currentIndex && (
              <ReviewStep
                step={translationIndex + 1}
                total={countByReviewType}
                translation={fieldTranslation}
                listValueIndex={currentIndex.listValue}
                referenceFields={referenceFields}
                inputs={inputs}
                selectedTranslated={translatedField}
                selectedListValue={translatedListValue}
                showExcludeWarning={showExcludeWarning}
                onTranslatedChange={setTranslatedField}
                onListValueChange={setTranslatedListValue}
              />
            )
          )}

          <div
            className={cn('flex gap-3 px-14 py-12', {
              'justify-between': isInitialStep,
            })}
          >
            {isInitialStep ? (
              <>
                <Button variant="outline" onClick={handleExplicitClose}>
                  {t`Close`}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setIsInitialStep(false)
                  }}
                >
                  {t`Let's go`}
                </Button>
              </>
            ) : (
              <>
                <Tooltip
                  title={t`By clicking 'done for now,' you will lose any changes made on this review screen.`}
                  className="text-center"
                >
                  <Button onClick={handleDone} variant="text" className="group">
                    <span className="group-hover:underline">{t`Done for now`}</span>
                  </Button>
                </Tooltip>
                <div className="grow" />
                <Tooltip
                  title={`‘${isListValueEditing ? 'Translated list value' : 'Translated field name'}’ must be empty to exclude`}
                  className="text-center"
                  disabled={!hasTranslatedValue}
                >
                  <Button
                    onClick={onExcludeClick}
                    variant="outline"
                    disabled={hasTranslatedValue}
                    isLoading={isExcludeTranslationFieldPending || isExcludeListValuePending}
                  >
                    {t`Exclude`}
                  </Button>
                </Tooltip>
                <Button variant="outline" onClick={handleSkip}>
                  {t`Skip`}
                </Button>
                <Button
                  variant="primary"
                  onClick={onNextClick}
                  isLoading={isUpdateTranslationFieldPending || isUpdateListValuePending}
                  disabled={!hasTranslatedValue}
                >
                  {translationIndex === countByReviewType - 1 ? 'Finish review' : 'Next'}
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </Modal>
  )
}

const getFieldTranslation = (translationData: TranslationData, translationIndex: number) =>
  translationData.translations[translationData.indexes[translationIndex]!.field]!

const getListValueTranslation = (fieldTranslation: ListFieldTranslation, listValueIndex: number) =>
  fieldTranslation.listValueTranslations[listValueIndex]!

const getTranslationReviewTypeState = (translation: Translation): Record<ReviewType, boolean> => ({
  [ReviewType.ALL]: true,
  [ReviewType.MATCH]: translation.status === TranslationStatus.Match,
  [ReviewType.NO_MATCH]: translation.status === TranslationStatus.NoMatch,
  [ReviewType.REVIEWED]: translation.status === TranslationStatus.Reviewed,
  [ReviewType.ALL_EXCLUDE_CONFIDENCE]: translation.confidence < 1,
  [ReviewType.ALL_EXCLUDE_REVIEWED]: translation.status !== TranslationStatus.Reviewed,
})

const getAppliableReviewTypes = (translation: Translation) =>
  Object.entries(getTranslationReviewTypeState(translation))
    .filter(([, state]) => state)
    .map(([reviewType]) => reviewType as ReviewType)

interface TranslationIndex {
  field: number
  listValue?: number
}

interface TranslationData {
  translations: FieldTranslation[]
  indexes: TranslationIndex[]
}

const EMPTY_TRANSLATION_DATA: TranslationData = {
  translations: [],
  indexes: [],
}
